import { getErrorMessage } from '@/api/errors'
import { DynamicField } from '@/components/projects/workflows/shared/DynamicField'
import { buildDefaults } from '@/components/projects/workflows/shared/dynamicFieldUtils'
import type { FormValues } from '@/components/projects/workflows/shared/dynamicFieldUtils'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { useContextSources, useStartChain } from '@/hooks'
import { parseChainYaml } from '@/lib/chainYaml'
import { extractChainRunId, extractValidationMessages } from '@/lib/validationErrors'
import type { ChainInputModel, ContextSourceListItem, TriggerInputField } from '@/types'
import { Play, X } from 'lucide-react'
import { useEffect, useMemo, useRef } from 'react'
import { useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'

function toTriggerInputField(input: ChainInputModel): TriggerInputField {
  return {
    name: input.name,
    type: input.type,
    required: input.required ?? false,
    default: input.default ?? null,
    description: input.description ?? null,
    options: input.options ?? null,
    multiSelect: input.multiSelect,
    contextType: input.contextType ?? null,
    connectorType: input.connectorType ?? null,
  }
}

interface StartChainDialogChain {
  id: string
  displayName: string
  yamlContent: string
}

interface StartChainDialogProps {
  projectId: string
  chain: StartChainDialogChain | null
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function StartChainDialog({ projectId, chain, open, onOpenChange }: StartChainDialogProps) {
  const navigate = useNavigate()
  const startChain = useStartChain(projectId)

  const { fields, definitionUnreadable } = useMemo(() => {
    if (!chain) return { fields: [] as TriggerInputField[], definitionUnreadable: false }
    try {
      const model = parseChainYaml(chain.yamlContent)
      return { fields: model.inputs.map(toTriggerInputField), definitionUnreadable: false }
    } catch {
      return { fields: [] as TriggerInputField[], definitionUnreadable: true }
    }
  }, [chain])

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<FormValues>({ mode: 'onBlur' })

  const hasContextPickerMultiSelect = useMemo(
    () => fields.some(field => field.type === 'context-picker' && field.multiSelect),
    [fields],
  )
  const { data: contextDataForDefaults, isLoading: contextSourcesLoading } = useContextSources(
    projectId,
    { maxResultCount: 1000 },
    undefined,
    { enabled: open && hasContextPickerMultiSelect },
  )
  const allContextItems: ContextSourceListItem[] = useMemo(
    () => contextDataForDefaults?.items ?? [],
    [contextDataForDefaults],
  )

  const defaultsSessionRef = useRef<string | null>(null)

  useEffect(() => {
    if (!open) {
      defaultsSessionRef.current = null
      return
    }
    if (hasContextPickerMultiSelect && contextSourcesLoading) return

    const sessionKey = chain?.id ?? ''
    if (defaultsSessionRef.current === sessionKey) return

    defaultsSessionRef.current = sessionKey
    reset(buildDefaults(fields, allContextItems))
  }, [open, chain?.id, fields, hasContextPickerMultiSelect, contextSourcesLoading, allContextItems, reset])

  const goToChainRun = (chainRunId: string) => {
    onOpenChange(false)
    navigate(`/projects/${projectId}/workflows/runs/chains/${chainRunId}`)
  }

  const onSubmit = async (values: FormValues) => {
    if (!chain || definitionUnreadable) return

    const provided = Object.fromEntries(
      Object.entries(values).filter(([, value]) => (value ?? '').trim() !== ''),
    )
    const inputValuesJson = Object.keys(provided).length > 0 ? JSON.stringify(provided) : undefined

    try {
      const response = await startChain.mutateAsync({ id: chain.id, data: { inputValuesJson } })
      toast.success(`Started "${chain.displayName}"`)
      goToChainRun(response.chainRunId)
    } catch (error) {
      const validationMessages = extractValidationMessages(error)
      const message =
        validationMessages.length > 0
          ? validationMessages.join(' ')
          : getErrorMessage(error) ?? 'Failed to start chain.'

      const pausedChainRunId = extractChainRunId(error)
      if (pausedChainRunId) {
        toast.error(validationMessages[0] ?? message)
        goToChainRun(pausedChainRunId)
        return
      }

      toast.error(message)
    }
  }

  if (!chain) return null

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[640px] rounded-2xl p-8 gap-6 border-none shadow-elevated" hideCloseButton>
        <DialogHeader className="flex-row items-center justify-between space-y-0">
          <DialogTitle className="text-2xl font-bold leading-[1.3] tracking-[0.015em] text-foreground">Start Chain</DialogTitle>
          <DialogClose className="rounded-sm opacity-70 transition-opacity hover:opacity-100 focus:outline-none">
            <X className="h-6 w-6" />
            <span className="sr-only">Close</span>
          </DialogClose>
        </DialogHeader>
        <DialogDescription className="text-sm font-normal leading-[1.6] tracking-[0.015em] text-foreground">
          Start a run of "<span className="font-bold">{chain.displayName}</span>". The first link is triggered immediately.
        </DialogDescription>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {definitionUnreadable && (
            <p role="alert" className="text-sm text-destructive" data-testid="start-chain-unreadable">
              This chain's definition could not be read — fix the YAML before starting.
            </p>
          )}

          {fields.length > 0 && (
            <div className="max-h-[55vh] space-y-6 overflow-y-auto pr-1" data-testid="start-chain-inputs">
              {fields.map((field) => (
                <DynamicField
                  key={field.name}
                  field={field}
                  projectId={projectId}
                  register={register}
                  errors={errors}
                  watch={watch}
                  setValue={setValue}
                />
              ))}
            </div>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpenChange(false)}
              className="h-10 min-w-10 rounded-lg py-2.5 px-4 gap-2.5 font-bold"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={definitionUnreadable || startChain.isPending}
              className="h-10 min-w-10 rounded-lg py-2.5 px-4 gap-2.5 font-bold [&_svg]:size-6 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="start-chain-confirm"
            >
              <Play className="h-6 w-6" />
              {startChain.isPending ? 'Starting…' : 'Start Chain'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
